import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { WalletTopUp } from "./iap";
import { PhotoViewer } from "./PhotoViewer";
import { BackupMenu } from "./BackupMenu";
import { BoardMenu } from "./BoardMenu";
import {
  store, CADENCES, POINT_PRESETS, emojiForChore, cadenceLabel, prettyDate, relativeDay, choreDueStatus, cadencePeriodDays,
  periodTally, totalPoints, currentStreak, todayLocal, rotationOrder, nextInRotation,
  FAIR_SHARE_GOAL_KEY, FAIR_SHARE_GOAL_DEFAULT, clampGoal, overShare, deadlineStatus,
  type Member, type Chore, type CompletionView, type MemberSignals,
} from "./store";
import { weeklyRecap, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import {
  Sparkles, Trash2, Lock, WifiOff, RefreshCw, Plus, Check, Flame, Trophy,
} from "lucide-react";

// Oriora Chores — a chore tracker for anyone: yourself, a partner, your kids, any household
// member (each is just a name you add). Each person has chores (name, point value, optional
// cadence); you log a completion (who did which chore, when, points earned) — optionally with a
// photo proof attached in chat. The AI Weekly Recap NARRATES the logged data — it never judges a
// person, never recommends rewards/punishment, never gives personal advice.

const PERIOD_DAYS = 7;

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2 #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-chores://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-chores://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented) {
    return <AiConsent brand="Oriora Chores"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed) return <Login />;
  return <Home email={email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ marginBottom: "0.4rem", display: "flex", justifyContent: "center" }}><svg width="44" height="44" viewBox="0 0 1024 1024" aria-hidden style={{ display: "block", flexShrink: 0 }}>
          <g fill="none" stroke="var(--accent)" strokeWidth="86" strokeLinecap="round">
            <path d="M300 512a212 212 0 0 1 212-212c78 0 147 42 184 105" />
            <path d="M724 512a212 212 0 0 1-212 212c-78 0-147-42-184-105" />
          </g>
          <path d="M700 356l24 96 -100 8z" fill="var(--accent)" />
          <path d="M324 668l-24-96 100-8z" fill="var(--accent)" />
        </svg></div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Oriora Chores</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>The board — who's carrying what</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Track chores for whoever you like —
          yourself, a partner, your kids. Everything lives on
          this device; the Weekly Recap runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-chores");
              else if (p === "macos") await signInMac("oriora-chores");
              else if (p === "web") window.location.href = "/login?next=/oriora-chores/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [chores, setChores] = useState<Chore[]>([]);
  const [completions, setCompletions] = useState<CompletionView[]>([]);
  const [loaded, setLoaded] = useState(false);

  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [showChore, setShowChore] = useState(false);
  const [editingChore, setEditingChore] = useState<Chore | null>(null);
  const [logForChore, setLogForChore] = useState<Chore | null>(null);   // quick-log a specific chore
  const [showLogPicker, setShowLogPicker] = useState(false);            // the in-flow "+ Log done"
  const [editingCompletion, setEditingCompletion] = useState<CompletionView | null>(null);
  const [confirmDelChore, setConfirmDelChore] = useState<Chore | null>(null);
  const [confirmDelCompletion, setConfirmDelCompletion] = useState<CompletionView | null>(null);
  const [confirmDelMember, setConfirmDelMember] = useState<Member | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  // The household's explicit fair-share goal (max % of the week any one person should carry).
  const [fairGoal, setFairGoal] = useState<number>(FAIR_SHARE_GOAL_DEFAULT);
  useEffect(() => {
    void (async () => {
      try {
        const raw = await store.getSetting(FAIR_SHARE_GOAL_KEY);
        if (raw !== null) setFairGoal(clampGoal(Number(raw)));
      } catch { /* unset or unavailable — the default stands */ }
    })();
  }, []);
  const saveFairGoal = useCallback(async (pct: number) => {
    const v = clampGoal(pct);
    setFairGoal(v);                                  // optimistic: the bar re-flags immediately
    try { await store.setSetting(FAIR_SHARE_GOAL_KEY, String(v)); } catch { /* stays for this session */ }
  }, []);

  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const active = useMemo(() => members.find((c) => c.id === activeId) ?? null, [members, activeId]);
  const activeChores = useMemo(() => chores.filter((c) => c.active !== 0), [chores]);
  const recapRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  // Two surfaces: the household BOARD is the home; a per-person page is a drill-down reached by
  // tapping a name on the board. Home is never a single person's scoreboard (a3 restructure).
  const [view, setView] = useState<"board" | "person">("board");
  const openPerson = useCallback((id: string) => { setActiveId(id); setView("person"); }, []);

  // 🔴 ORPHAN-VIEW GUARD. The person page is a drill-down into a member that can stop existing
  // underneath it — delete the last person (person page → Delete person), delete one from chat,
  // or restore a backup that doesn't contain them. `activeId` then falls to null while `view`
  // is still "person", and the render below (`view === "board" ? … : active ? … : null`) drew
  // NOTHING: a blank screen with no board, no back button and no way out but force-quitting the
  // app (found live 2026-08-28, `_verify-shots/restore/3b-after-delete.png`; Guideline 2.1(a)
  // dead end). The view must never outlive its subject — pop back to the board.
  useEffect(() => { if (view === "person" && loaded && !active) setView("board"); }, [view, loaded, active]);

  const loadMembers = useCallback(async () => {
    const list = await store.listMembers();
    setMembers(list);
    setActiveId((cur) => (cur && list.some((c) => c.id === cur) ? cur : (list[0]?.id ?? null)));
    setLoaded(true);
  }, []);
  useEffect(() => { void loadMembers(); }, [loadMembers]);

  const loadMemberData = useCallback(async (memberId: string | null) => {
    if (!memberId) { setChores([]); setCompletions([]); return; }
    const [chs, comps] = await Promise.all([store.listChores(memberId), store.listCompletions(memberId)]);
    setChores(chs);
    setCompletions(comps);
  }, []);
  useEffect(() => { void loadMemberData(activeId); }, [activeId, loadMemberData]);

  // THE BOARD needs every member's chores at once, not just the selected one — the whole point is
  // that you stop tapping through people to find out what is outstanding. Both store calls take an
  // OPTIONAL memberId, so no argument returns the lot.
  const [allChores, setAllChores] = useState<Chore[]>([]);
  const [allCompletions, setAllCompletions] = useState<CompletionView[]>([]);
  const loadBoard = useCallback(async () => {
    const [chs, comps] = await Promise.all([store.listChores(), store.listCompletions()]);
    setAllChores(chs);
    setAllCompletions(comps);
  }, []);
  useEffect(() => { void loadBoard(); }, [loadBoard]);

  const reloadAll = useCallback(async () => { await loadMembers(); await loadBoard(); }, [loadMembers, loadBoard]);
  const reloadData = useCallback(async () => { await loadMemberData(activeId); await loadBoard(); }, [activeId, loadMemberData, loadBoard]);

  // Period + all-time tallies, computed client-side from already-loaded data (instant,
  // mirrors signals.rs). Plain counts/points — never a judgement of the member.
  const period = useMemo(() => periodTally(completions, PERIOD_DAYS), [completions]);
  const ptsAllTime = useMemo(() => totalPoints(completions), [completions]);
  const streak = useMemo(() => currentStreak(completions), [completions]);

  // Bring the AI recap to the home: recap the household's most-active person, on the board itself.
  // Same verbatim wallet call + fence — just surfaced where the app opens, not buried in a profile.
  const homeRecapMember = useMemo(() => mostActiveMember(members, allCompletions), [members, allCompletions]);
  const homeRecapCompletions = useMemo(
    () => (homeRecapMember ? allCompletions.filter((c) => c.member_id === homeRecapMember.id) : []),
    [homeRecapMember, allCompletions],
  );

  const doExport = useCallback(() => {
    if (!active || (chores.length === 0 && completions.length === 0)) return;
    const { csv, filename } = buildExport(active, chores, completions);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [active, chores, completions]);
  exportRef.current = doExport;

  // Quick-log a chore done TODAY for the active member (the in-flow per-chore "Done" button).
  // For a rotating chore, advance the turn to the next member in the pool after logging.
  const quickLog = useCallback(async (chore: Chore) => {
    await store.createCompletion({ member_id: chore.member_id, chore_id: chore.id });
    const next = nextInRotation(chore);
    if (next) await store.updateChore(chore.id, { member_id: next });
    await reloadData();
  }, [reloadData]);

  // Native-menu actions (macOS Actions menu): Log / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "log": setShowLogPicker(true); break;
        case "recap":
          recapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          recapRef.current?.querySelector<HTMLButtonElement>("button")?.click();
          break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Add a chore from the board (no person selected yet): fall back to the first person so the
  // ChoreForm — which is authored per-person — always has a member to attach to.
  function startAddChore() {
    if (!activeId && members[0]) setActiveId(members[0].id);
    setEditingChore(null); setShowChore(true);
  }

  async function delChore(id: string) { setConfirmDelChore(null); await store.deleteChore(id); await reloadData(); }
  async function delCompletion(id: string) { setConfirmDelCompletion(null); await store.deleteCompletion(id); await reloadData(); }
  async function delMember(id: string) { setConfirmDelMember(null); await store.deleteMember(id); await reloadAll(); }

  if (!loaded) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;

  // Header content for the identity pass: the board leads with THIS household's week, not a
  // wordmark. Kept deliberately durable — it states the week and what is outstanding, so it stays
  // true as the app grows rather than pinning a field list that rots.
  const weekLabel = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  const openCount = chores.length;
  const boardLede = members.length === 0
    ? "Set up your board"
    : openCount === 0
      ? "Nothing on the board yet"
      : `${openCount} ${openCount === 1 ? "chore" : "chores"} on the board`;

  return (
    <main style={{
      maxWidth: 760, margin: "0 auto",
      // 🔴 The chat entry is `position: fixed` at the bottom (now `.och-marker`, the docked
      // marker — was `.och-launch`, the floating pill, replaced by the 2026-08-30 identity pass),
      // so it floats OVER whatever sits last on the page — it was seen covering the full-width
      // "Log done" button in the 760px verification shot. Reserve its height (~48px) + its own
      // bottom gap so the last control stays reachable. A fixed overlay needs the page to make
      // room; moving the button would only shift the problem to whatever ends up last.
      padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) calc(2rem + 64px + env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="notice notice-warn" style={{ marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 1 }} /> Weekly Recap paused — your entries still save locally
        </div>
      )}

      {/* HEADER — identity pass 2026-08-30, per DESIGN-BRIEF-IDENTITY-2026-08-29.
          Was: wordmark + logo left, two circular controls right — the exact shape ~15 sibling apps
          share, and the reason a reviewer flipping screenshots sees one app repeated.
          Now: the household's own week leads (this app's a3 promise is the BOARD), and the two
          circles collapse into ONE square marker-tray control docked to the board frame.
          BoardMenu wraps the shared AccountMenu/BackupMenu UNCHANGED — the account-deletion flow
          the 5.1.1(v) gate depends on is untouched, just reached through one control. */}
      <header className="row och-frame" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.1rem" }}>
        <div>
          <div className="och-week">{weekLabel}</div>
          <h1 className="och-lede" style={{ margin: "0.15rem 0 0" }}>{boardLede}</h1>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BoardMenu>
          {/* Add funds shown DIRECTLY in the drawer (not only inside the account avatar's popover)
              so the In-App Purchases are locatable — Apple 2.1(b) rejected Focus/Gifted v1.0 when
              they lived only two levels deep (drawer → account avatar → popover), 2026-09-05. */}
          <WalletTopUp />
          <div style={{ height: 1, background: "var(--border)", margin: "0.5rem 0" }} />
          <BackupMenu
            appName="Chores" fileSlug="chores" appScheme="oriora-chores"
            hasData={members.length > 0} onRestored={async () => { await reloadAll(); await reloadData(); }}
          />
          <AccountMenu email={email} appSlug="oriora-chores" appName="Oriora Chores" onSignedOut={onSignedOut} />
          </BoardMenu>
        </div>
      </header>

      {view === "board" ? (
        <Board
          fairGoal={fairGoal}
          onFairGoal={saveFairGoal}
          members={members}
          chores={allChores}
          completions={allCompletions}
          onOpenPerson={openPerson}
          onAddMember={() => { setEditingMember(null); setShowAddMember(true); }}
          onAddChore={startAddChore}
          onRebalance={() => setChatOpen(true)}
          onQuickLog={quickLog}
          onStarted={reloadAll}
          recapMember={homeRecapMember}
          recapCompletions={homeRecapCompletions}
          recapRef={recapRef}
        />
      ) : active ? (
        <>
          {/* Per-person page — a drill-down from a name on the board, never the home itself. */}
          <button className="btn-ghost row" style={{ padding: "0.3rem 0.4rem", marginBottom: "0.4rem", gap: "0.35rem" }} onClick={() => setView("board")}>
            ← <span>The board</span>
          </button>

          <PeopleSwitcher
            members={members}
            activeId={activeId}
            onPick={setActiveId}
            onAdd={() => { setEditingMember(null); setShowAddMember(true); }}
          />

          <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginTop: "0.4rem" }}>
            <div className="serif" style={{ fontSize: "1.6rem", lineHeight: 1.1 }}>{active.name}</div>
            <div className="row" style={{ gap: "0.3rem" }}>
              <button className="btn btn-sm" onClick={() => { setEditingMember(active); setShowAddMember(true); }}>Edit</button>
              <button className="btn btn-sm" title="Delete person" aria-label="Delete person" onClick={() => setConfirmDelMember(active)}><Trash2 size={13} /></button>
            </div>
          </div>

          <PersonStats points={ptsAllTime} period={period} streak={streak} />

          {/* In-flow primary action — NEVER a position:fixed FAB. */}
          <button className="btn btn-primary" disabled={activeChores.length === 0} onClick={() => setShowLogPicker(true)}
            style={{ width: "100%", marginTop: "0.85rem", borderRadius: 999, padding: "0.8rem 1.4rem", fontSize: "var(--fs-sm)", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}>
            <Plus size={16} /> Log done
          </button>

          <div ref={recapRef}>
            <WeeklyRecapCard member={active} completions={completions} />
          </div>

          {/* Chores */}
          <SectionHeader
            title={`${active.name}'s chores`}
            count={activeChores.length}
            action={<button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.6rem", gap: "0.3rem" }} onClick={() => { setEditingChore(null); setShowChore(true); }}><Plus size={12} /> Add chore</button>}
          />
          {activeChores.length === 0 ? (
                <div className="card empty-glow" style={{ padding: "1.75rem 1.25rem", textAlign: "center" }}>
                  <div className="empty-art" style={{ marginBottom: "0.6rem" }}>🧹</div>
                  <div className="serif" style={{ fontSize: "1.1rem", margin: "0 0 0.2rem" }}>Set up {active.name}'s first chore</div>
                  <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Name it, set the points, pick how often. Then log it done in a tap.</div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
                  {activeChores.map((ch) => {
                    const done = completions.filter((c) => c.chore_id === ch.id);
                    const last = done[0]?.done_on ?? null;
                    return (
                      <ChoreTile
                        key={ch.id} chore={ch} doneCount={done.length} lastDoneOn={last} allMembers={members}
                        onLog={() => quickLog(ch)}
                        onEdit={() => { setEditingChore(ch); setShowChore(true); }}
                        onDelete={() => setConfirmDelChore(ch)}
                      />
                    );
                  })}
                </div>
              )}

              {/* Logged completions */}
              <SectionHeader
                title="Logged done"
                count={completions.length}
                action={
                  <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
                    {completions.length > 0 && <button className="btn" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.6rem" }} onClick={doExport}>Export CSV</button>}
                  </div>
                }
              />
              {completions.length === 0 ? (
                <div className="card" style={{ padding: "1.5rem 1.25rem", textAlign: "center" }}>
                  <div style={{ fontSize: "2.2rem" }}>📋</div>
                  <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: "0.4rem" }}>Nothing logged yet. Tap “Log done” when {active.name} finishes a chore.</div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
                  {completions.slice(0, 50).map((c) => (
                    <CompletionTile key={c.id} c={c} onEdit={() => setEditingCompletion(c)} onDelete={() => setConfirmDelCompletion(c)} />
                  ))}
                </div>
              )}
        </>
      ) : null}

      {/* ── Modals ── */}
      {showAddMember && (
        <MemberForm
          editing={editingMember}
          onClose={() => { setShowAddMember(false); setEditingMember(null); }}
          onSaved={async (savedId) => { setShowAddMember(false); setEditingMember(null); await reloadAll(); if (savedId) setActiveId(savedId); }}
        />
      )}
      {showChore && active && (
        <ChoreForm
          member={active}
          allMembers={members}
          editing={editingChore}
          onClose={() => { setShowChore(false); setEditingChore(null); }}
          onSaved={async () => { setShowChore(false); setEditingChore(null); await reloadData(); }}
        />
      )}
      {showLogPicker && active && (
        <LogPicker
          member={active}
          chores={activeChores}
          onClose={() => setShowLogPicker(false)}
          onPick={(ch) => { setShowLogPicker(false); setLogForChore(ch); }}
          onAddChore={() => { setShowLogPicker(false); setEditingChore(null); setShowChore(true); }}
        />
      )}
      {logForChore && active && (
        <LogForm
          member={active}
          chore={logForChore}
          onClose={() => setLogForChore(null)}
          onSaved={async () => { setLogForChore(null); await reloadData(); }}
        />
      )}
      {editingCompletion && active && (
        <LogForm
          member={active}
          completion={editingCompletion}
          onClose={() => setEditingCompletion(null)}
          onSaved={async () => { setEditingCompletion(null); await reloadData(); }}
        />
      )}

      {confirmDelChore && (
        <ConfirmDelete
          title="Delete this chore?"
          body={`Delete “${confirmDelChore.name}” and ALL of its logged completions for ${active?.name ?? "this person"}? This can't be undone. (To keep the history, edit the chore and set it to retired instead.)`}
          onCancel={() => setConfirmDelChore(null)}
          onConfirm={() => delChore(confirmDelChore.id)}
        />
      )}
      {confirmDelCompletion && (
        <ConfirmDelete
          title="Delete this entry?"
          body={`Remove the “${confirmDelCompletion.chore_name}” logged on ${prettyDate(confirmDelCompletion.done_on)}? This can't be undone.`}
          onCancel={() => setConfirmDelCompletion(null)}
          onConfirm={() => delCompletion(confirmDelCompletion.id)}
        />
      )}
      {confirmDelMember && (
        <ConfirmDelete
          title={`Delete ${confirmDelMember.name}?`}
          body={`This removes ${confirmDelMember.name} and ALL their chores and logged completions from this device. This can't be undone.`}
          onCancel={() => setConfirmDelMember(null)}
          onConfirm={() => delMember(confirmDelMember.id)}
        />
      )}

      {/* ── App Brain chat (additive) ── */}
      {/* CHAT ENTRY — identity pass 2026-08-30. Was a floating rounded pill labelled "Chat",
          bottom-right, identical in ~15 sibling apps: the single loudest fingerprint.
          Now a marker docked INTO the board frame, written in the board's own language ("write on
          the board") rather than naming the mechanism.
          🔴 The entry is REPLACED, never removed. The chat is how this app's AI is reached, and an
          app whose headline capability has no route to it is a 2.1 hollow-feature rejection. */}
      {!chatOpen && (
        <button className="och-marker" onClick={() => setChatOpen(true)} aria-label="Write on the board — add, log, find or recap">
          <span className="och-marker-nib" aria-hidden />
          write on the board
        </button>
      )}
      <aside className={`och-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="och-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, log, find, or recap</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat memberId={activeId} memberName={active?.name ?? null} onChanged={async () => { await reloadAll(); await reloadData(); }} />
      </aside>
    </main>
  );
}

// ── Identity helpers (deterministic, no schema field) ────────────────────────────
/** Stable 0–5 palette slot for a person, from their id — drives the .avN swatch classes. */
function avatarClass(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `av${h % 6}`;
}
/** 1–2 letter initials for an avatar. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
/** The household's most-active person (most completions, ties broken by most recent), for the
 *  home recap. Null if nobody has logged anything yet. */
function mostActiveMember(members: Member[], completions: CompletionView[]): Member | null {
  const counts = new Map<string, number>();
  const recent = new Map<string, string>();
  for (const c of completions) {
    counts.set(c.member_id, (counts.get(c.member_id) ?? 0) + 1);
    const prev = recent.get(c.member_id);
    if (!prev || c.done_on > prev) recent.set(c.member_id, c.done_on);
  }
  let best: Member | null = null;
  for (const m of members) {
    if (!counts.get(m.id)) continue;
    if (!best) { best = m; continue; }
    const bc = counts.get(best.id) ?? 0, mc = counts.get(m.id) ?? 0;
    if (mc > bc || (mc === bc && (recent.get(m.id) ?? "") > (recent.get(best.id) ?? ""))) best = m;
  }
  return best;
}
/** The short "when" badge on a board row — how overdue, "Today", or the next-due weekday. */
function whenLabel(r: BoardRow): string {
  if (r.status === "overdue") return `−${r.daysOver}d`;
  if (r.status === "today") return "Today";
  // An outstanding deadline names the actual date rather than a cadence weekday — "by when"
  // is a calendar fact, so show the calendar.
  const dl = deadlineStatus(r.chore.due_on, r.lastDone);
  if (dl && dl.status === "ok") {
    const d = new Date(dayIndex(r.chore.due_on) * DAY_MS);   // same helper pair used below
    return `Due ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }
  const every = cadenceDays(r.chore.cadence);
  if (r.lastDone && every) {
    const d = new Date((dayIndex(r.lastDone) + every) * DAY_MS);
    return d.toLocaleDateString("en-US", { weekday: "short" });
  }
  return "Soon";
}
/** One-line fair-share narration for the split bar, measured against the household's GOAL.
 *  "" when there's nothing to compare. Purely observational — it states the split and the
 *  target, never judges a person. */
function fairnessNote(
  balance: { member: Member; points: number; share: number }[],
  goalPct: number,
): string {
  if (balance.length < 2) return "";
  const total = balance.reduce((a, b) => a + b.points, 0);
  if (total === 0) return "";
  const over = overShare(balance, goalPct);
  if (over.length > 0) {
    const o = over[0];             // balanceByMember returns points-descending, so this is the worst
    return `${o.member.name} is carrying ${Math.round(o.share * 100)}% this week — over the ${goalPct}% goal. Tap Rebalance to even it out.`;
  }
  const top = balance[0];
  return `Within the ${goalPct}% goal — ${top.member.name} is highest at ${Math.round(top.share * 100)}%.`;
}

/** The explicit distribution goal control: the household sets the ceiling, the bar flags it. */
function FairShareGoal({ goal, onChange }: { goal: number; onChange: (pct: number) => void }) {
  const CHOICES = [50, 60, 75, 100];
  return (
    <div className="row" style={{ gap: "0.35rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.5rem" }}>
      <span className="muted" style={{ fontSize: 10 }}>No one over</span>
      {CHOICES.map((c) => (
        <button
          key={c}
          type="button"
          className="chip"
          aria-pressed={goal === c}
          onClick={() => onChange(c)}
          style={{
            padding: "0.1rem 0.45rem", fontSize: 10, cursor: "pointer",
            background: goal === c ? "var(--accent)" : undefined,
            borderColor: goal === c ? "var(--accent)" : undefined,
            color: goal === c ? "#fff" : undefined,
          }}
        >
          {c === 100 ? "Off" : `${c}%`}
        </button>
      ))}
    </div>
  );
}

// ── THE BOARD (home) — the ranked household work IS the page. Structurally unlike any sibling:
//    no gradient hero, no per-person size-grid, no segmented switcher up front. ────────────────
// ── READY-STATE 2026-09-19 — the FIRST screen's door (zero people, zero chores) ──────────────
// The board's structure is already on the screen above this — the week's split line, the header.
// What was missing was a way to put the first thing ON it: the old shape was a two-step ladder
// (person, then chore) behind two separate forms. One line does both — the chore, and who it is
// on — because a board with a chore and nobody, or a person and nothing to do, is not a board.
// The "who" defaults to "Me" so a household of one never has to invent a name for themselves.
// ⚠️ GENERAL, never a role: the copy names no family, kids, parents or partners as the frame.
// Local only: two existing store calls, no lookup, no AI — the billed weekly recap is untouched.
function StartBoard({ onStarted, onAddPerson }: { onStarted: () => Promise<void> | void; onAddPerson: () => void }) {
  const [chore, setChore] = useState("");
  const [who, setWho] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function start() {
    const c = chore.trim();
    if (!c) { setErr("Name the chore — points and how often can wait."); return; }
    setBusy(true); setErr(null);
    try {
      const m = await store.createMember({ name: who.trim() || "Me" });
      await store.createChore({ member_id: m.id, name: c, points: 1, cadence: "weekly" });
      await onStarted();
    } catch (e) {
      setErr(`Couldn't write that on the board — ${e instanceof Error ? e.message : String(e)}`);
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: "0.6rem" }}>
      <div className="micro">The board is empty</div>
      <p className="start-lede">
        Write the first thing on it — what needs doing, and who it is on. Everything else (points,
        how often, whose turn next) can be changed once it is up there.
      </p>

      <div className="start-row">
        <input value={chore} onChange={(e) => setChore(e.target.value)} aria-label="Chore"
          placeholder="Chore — e.g. take the bins out" className="start-chore"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void start(); } }} />
        <input value={who} onChange={(e) => setWho(e.target.value)} aria-label="Who it's on"
          placeholder="Me" className="start-who"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void start(); } }} />
      </div>

      {err && <div className="start-err">{err}</div>}

      <button className="btn btn-primary start-go" disabled={busy} onClick={() => void start()}>
        {busy ? "Writing…" : "Put it on the board"}
      </button>

      <p className="start-foot">
        {/* §7b: "there is no account to share it through" reads as "no account" while sign-in IS
            required, and the device claim was unqualified though Cloud Backup ships. Rewritten
            2026-09-25 to say what it means: the board is not shared through anyone's account. */}
        The board stays on this device unless you back it up, and it is never shared through an
        account — no ads on it, and nothing to subscribe to. Only the optional weekly recap costs
        anything, per use.
        {" "}
        <button type="button" className="start-alt" onClick={onAddPerson}>Add someone first instead</button>
      </p>
    </div>
  );
}

function Board({ fairGoal, onFairGoal, members, chores, completions, onOpenPerson, onAddMember, onAddChore, onRebalance, onQuickLog, onStarted, recapMember, recapCompletions, recapRef }: {
  fairGoal: number; onFairGoal: (pct: number) => void;
  members: Member[]; chores: Chore[]; completions: CompletionView[];
  onOpenPerson: (id: string) => void; onAddMember: () => void; onAddChore: () => void;
  /** Reload once the first chore (and the person carrying it) exist. */
  onStarted: () => Promise<void> | void;
  onRebalance: () => void; onQuickLog: (chore: Chore) => void;
  recapMember: Member | null; recapCompletions: CompletionView[];
  recapRef: React.RefObject<HTMLDivElement>;
}) {
  const rows = useMemo(() => boardRows(chores, completions, members), [chores, completions, members]);
  const balance = useMemo(() => balanceByMember(completions, members), [completions, members]);
  const outstanding = rows.filter((r) => r.status !== "settled");
  const activeChoreCount = chores.filter((c) => c.active !== 0).length;
  const totalShare = balance.reduce((a, b) => a + b.points, 0);

  const GROUPS: { key: DueStatus; label: string; tone: string }[] = [
    { key: "overdue", label: "Overdue", tone: "var(--danger)" },
    { key: "today", label: "Due now", tone: "var(--accent)" },
    { key: "soon", label: "Coming up", tone: "var(--ink-3)" },
  ];

  return (
    <div>
      {/* Board headline — leads with the promise ("what needs doing, whose turn"), not a person. */}
      <div style={{ marginBottom: "1.1rem" }}>
        <div className="board-eyebrow">Oriora · The board</div>
        <div className="board-title" style={{ marginTop: 3 }}>The board</div>
        <div className="board-sub">
          {members.length === 0
            ? "fenced to this device"
            : `${members.length} ${members.length === 1 ? "person" : "people"} on the board · fenced to this device`}
        </div>
      </div>

      {/* THIS WEEK'S SPLIT — fair-share promoted into the board's frame (Tody paywalls this). */}
      <div style={{ marginBottom: "1.25rem" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.5rem" }}>
          <span className="micro">This week's split</span>
          {members.length > 1 && totalShare > 0 && (
            <button onClick={onRebalance} className="micro" style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer" }}>Rebalance</button>
          )}
        </div>
        {totalShare > 0 ? (
          <>
            <div className="split-bar">
              {balance.filter((b) => b.points > 0).map((b) => (
                <span key={b.member.id} className={`av ${avatarClass(b.member.id)}`}
                  title={`${b.member.name} · ${b.points} pt`}
                  style={{ width: `${Math.round(b.share * 100)}%`, borderRadius: 0 }} />
              ))}
            </div>
            <div style={{ fontSize: "var(--fs-xs)", color: overShare(balance, fairGoal).length > 0 ? "var(--danger)" : "var(--ink-2)", marginTop: "0.5rem", lineHeight: 1.4 }}>{fairnessNote(balance, fairGoal)}</div>
            {members.length > 1 && <FairShareGoal goal={fairGoal} onChange={onFairGoal} />}
          </>
        ) : (
          <>
            <div className="split-empty" />
            <div style={{ fontSize: "var(--fs-xs)", color: "var(--ink-3)", marginTop: "0.5rem", lineHeight: 1.4 }}>
              {/* READY-STATE: this used to read "Add people to start tracking…", which described
                  the two-step ladder that no longer exists — the door below writes the chore and
                  whoever it is on in one go. It now says what the line itself does. */}
              {members.length === 0 ? "This line fills in as chores get ticked off — it is who is carrying what." : "Log a few chores to see who's carrying what."}
            </div>
          </>
        )}
      </div>

      {/* States: no people → onboarding · people but no chores → add a chore · else the ranked board */}
      {members.length === 0 ? (
        /* READY-STATE 2026-09-19 — this was a two-step onboarding ladder (add a person, THEN a
           chore, step 2 greyed out), so nothing could be written on the board until two separate
           forms had been filled. The board's own structure is already on this screen above — the
           split line, the header — so what was missing was a way to put the first thing ON it. */
        <StartBoard onStarted={onStarted} onAddPerson={onAddMember} />
      ) : activeChoreCount === 0 ? (
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <div className="micro">The board is empty</div>
          <button className="step-row active" onClick={onAddChore}>
            <span className="step-num" style={{ background: "var(--accent)", color: "var(--on-accent)" }}>+</span>
            <span><div style={{ fontWeight: 600 }}>Add a chore</div><div style={{ fontSize: "var(--fs-xs)", color: "var(--ink-2)" }}>Name it, set the points and how often — we'll rank it by what's owed.</div></span>
          </button>
        </div>
      ) : outstanding.length === 0 ? (
        <div style={{ textAlign: "center", padding: "1.5rem 1rem" }}>
          <div style={{ fontSize: "2rem" }}>🎉</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: "0.4rem" }}>Nothing outstanding across the household.</div>
        </div>
      ) : (
        GROUPS.map(({ key, label, tone }) => {
          const group = rows.filter((r) => r.status === key);
          if (group.length === 0) return null;
          return (
            <div key={key}>
              <div className="group-head">
                <span className="group-dot" style={{ background: tone }} />
                <span className="micro">{label} · {group.length}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.4rem" }}>
                {group.map((r) => (
                  <div key={r.chore.id} className="board-row">
                    {/* Tapping the row body drills into whose turn it is. */}
                    <button onClick={() => r.owner && onOpenPerson(r.owner.id)}
                      style={{ display: "flex", alignItems: "center", gap: "0.6rem", flex: 1, minWidth: 0, background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer" }}>
                      <span className="board-when" style={{ color: tone }}>{whenLabel(r)}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontWeight: 600, fontSize: "var(--fs-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.chore.name}</span>
                        <span style={{ display: "block", fontSize: "var(--fs-xs)", color: "var(--ink-3)" }}>
                          {cadenceLabel(r.chore.cadence)}{r.chore.points ? ` · ${r.chore.points} pt` : ""}
                          {r.owner ? ` · ${r.rotating ? `${r.owner.name}'s turn` : r.owner.name}` : ""}
                        </span>
                      </span>
                      {r.owner && (
                        <span className={`av ${avatarClass(r.owner.id)}`}
                          title={r.rotating ? `${r.owner.name}'s turn` : r.owner.name}
                          style={{ width: 26, height: 26, fontSize: 11, opacity: key === "soon" ? 0.7 : 1 }}>{initials(r.owner.name)}</span>
                      )}
                    </button>
                    {/* Tapping the ring logs it done today (advances rotation). */}
                    <button className="board-check" title={`Log “${r.chore.name}” done today`} aria-label={`Log ${r.chore.name} done`} onClick={() => onQuickLog(r.chore)}>
                      <Check size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* The ONE headline AI feature, on the home — verbatim wallet call + fence, scoped to the
          household's most-active person so the recap has something to narrate. */}
      {recapMember && (
        <div ref={recapRef} style={{ marginTop: "0.5rem" }}>
          <WeeklyRecapCard member={recapMember} completions={recapCompletions} />
        </div>
      )}
    </div>
  );
}

// ── People switcher (avatars) — used in the person drill-down, NOT on the board home ──────────
function PeopleSwitcher({ members, activeId, onPick, onAdd }: {
  members: Member[]; activeId: string | null; onPick: (id: string) => void; onAdd: () => void;
}) {
  return (
    <div style={{ display: "flex", gap: "0.9rem", overflowX: "auto", padding: "0.2rem 0 0.4rem", scrollbarWidth: "none" }}>
      {members.map((m) => {
        const on = m.id === activeId;
        return (
          <button key={m.id} onClick={() => onPick(m.id)}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.35rem", flexShrink: 0 }}>
            <span className={`av ${avatarClass(m.id)}`}
              style={{ width: on ? 52 : 44, height: on ? 52 : 44, fontSize: on ? 18 : 15, opacity: on ? 1 : 0.6, boxShadow: on ? "0 0 0 3px var(--accent-line)" : "none" }}>{initials(m.name)}</span>
            <span style={{ fontSize: "var(--fs-xs)", color: on ? "var(--ink-1)" : "var(--ink-3)", fontWeight: on ? 600 : 400, maxWidth: 64, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</span>
          </button>
        );
      })}
      <button onClick={onAdd} aria-label="Add a person"
        style={{ background: "none", border: "1.5px dashed var(--border-strong)", borderRadius: "50%", width: 44, height: 44, color: "var(--ink-3)", cursor: "pointer", flexShrink: 0, alignSelf: "flex-start", marginTop: 2 }}><Plus size={16} /></button>
    </div>
  );
}

// ── Person stat tiles — flat + bordered (NOT the sibling gradient hero + size-grid) ───────────
function PersonStats({ points, period, streak }: {
  points: number; period: { completions: number; points: number; activeDays: number }; streak: { days: number; live: boolean };
}) {
  return (
    <div className="pstat-grid" style={{ margin: "0.5rem 0 0.25rem" }}>
      <div className="pstat">
        <div className="cat"><Check size={13} /> This week</div>
        <div className="val tnum">{period.completions}</div>
        <div className="meta">{period.activeDays} active day{period.activeDays === 1 ? "" : "s"} · {period.points} pt</div>
      </div>
      <div className="pstat">
        <div className="cat"><Flame size={13} /> Streak</div>
        <div className="val tnum">{streak.days}</div>
        <div className="meta">{streak.days === 0 ? "no run yet" : `day${streak.days === 1 ? "" : "s"} in a row${streak.live ? " (incl. today)" : ""}`}</div>
      </div>
      <div className="pstat">
        <div className="cat"><Trophy size={13} /> Points</div>
        <div className="val tnum">{points}</div>
        <div className="meta">earned all time</div>
      </div>
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ title, count, action }: { title: string; count: number; action?: React.ReactNode }) {
  return (
    <div className="sec-head">
      <h2>{title} <span className="muted" style={{ fontSize: "var(--fs-xs)", fontWeight: 400 }}>{count}</span></h2>
      <div className="row" style={{ gap: "0.5rem" }}>
        {action}
        <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.25rem" }}><Lock size={11} /> on this device</span>
      </div>
    </div>
  );
}

// ── The ONE headline AI feature: Weekly Recap (observational, facts-only) ──────
type RecapErr = { code: BrainErrorCode | "empty"; message: string };

function WeeklyRecapCard({ member, completions }: { member: Member; completions: CompletionView[] }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<MemberSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  // Reset whenever the active member changes — the recap is per member.
  useEffect(() => { setText(null); setSignals(null); setErr(null); }, [member.id]);

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null);
    try {
      const sig = await store.computeSignals(member.id, todayLocal(), PERIOD_DAYS);
      const r = await weeklyRecap(sig);
      const recap = r.recap.trim();
      if (!recap) {
        setErr({ code: "empty", message: "Not enough logged yet to recap — log a few more chores over the week." });
      } else {
        setText(recap);
        setSignals(sig);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  if (completions.length === 0) return null;

  return (
    <div className="recap" style={{ padding: "1rem 1.25rem", margin: "1rem 0 0.25rem" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> Weekly Recap
        </div>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy} onClick={run}>
          {busy ? "Reading…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Recap the week"}
        </button>
      </div>

      {!busy && !text && !err && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
          Pulls together what {member.name} logged — chores done and points this week, the most- and least-logged chore, and any day streak.
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Reading {member.name}'s chore log…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "68%" }} />
        </div>
      )}

      {err && !busy && <BrainErrorView err={err} onRetry={run} />}

      {text && !busy && (
        <div className="fade-up" style={{ marginTop: "0.6rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <SignalEvidence signals={signals} />}
          {/* OBSERVATIONAL FENCE — on screen, every time. */}
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 10, lineHeight: 1.5 }}>
              Recaps only the chores you logged — facts about counts, points and dates. It does not judge anyone, recommend rewards or consequences, or give personal advice.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function BrainErrorView({ err, onRetry }: { err: RecapErr; onRetry: () => void }) {
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-warn" style={{ marginTop: 10 }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep using AI</div>
          {/* 3.1.1 / 3.1.3(b) anti-steering: an out-of-funds message must NOT name the website —
              steering the user off-platform to add funds is what Keycap, Oracle, Closet and
              Curio were rejected for. Wording matched to Stash, which ships this same
              component and passes. (The iap.tsx account link is separate and deliberate.) */}
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info" style={{ marginTop: 10 }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable";
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {retryable && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the actual derived numbers the recap drew from — purely observational.
function SignalEvidence({ signals }: { signals: MemberSignals }) {
  const chips: { label: string; title: string }[] = [];

  const p = signals.period;
  if (p.completions > 0) {
    chips.push({
      label: `${p.completions} this week · ${p.points} pt`,
      title: `${p.completions} chores logged across ${p.active_days} day${p.active_days === 1 ? "" : "s"} (${p.from} → ${p.to}), ${p.points} points`,
    });
  }

  const top = signals.per_chore.find((c) => c.total_count > 0);
  if (top) {
    chips.push({
      label: `top: ${top.name} ×${top.total_count}`,
      title: `“${top.name}” logged ${top.total_count} time${top.total_count === 1 ? "" : "s"} (${top.period_count} this week), ${top.total_points} points`,
    });
  }

  const quiet = signals.per_chore.find((c) => c.active && c.period_count === 0);
  if (quiet) {
    chips.push({
      label: `quiet: ${quiet.name}`,
      title: `“${quiet.name}” has no completions in the last ${signals.period_days} days`,
    });
  }

  if (signals.streak.current_days >= 2) {
    chips.push({
      label: `${signals.streak.current_days}-day streak`,
      title: `A chore logged ${signals.streak.current_days} days in a row${signals.streak.counts_from_today ? ", including today" : ""}`,
    });
  }

  if (signals.overall.days_span > 0) {
    chips.push({
      label: `${signals.overall.total_completions} all time`,
      title: `${signals.overall.total_completions} chores logged across ${signals.overall.days_span} days, ${signals.overall.total_points} points`,
    });
  }

  if (chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.6rem" }}>
      {chips.map((c) => (
        <span key={c.label} className="chip tnum" title={c.title}
          style={{ padding: "0.1rem 0.45rem", fontSize: 10, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)", minHeight: "auto" }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ── Chore tile (with an in-flow "Done" quick-log) ─────────────────────────────
function ChoreTile({ chore, doneCount, lastDoneOn, allMembers, onLog, onEdit, onDelete }: {
  chore: Chore; doneCount: number; lastDoneOn: string | null; allMembers: Member[]; onLog: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const turnName = chore.rotation ? (allMembers.find((c) => c.id === chore.member_id)?.name ?? null) : null;
  return (
    <div className="tile">
      <span className="tile-icon" title={chore.name}>{emojiForChore(chore.name)}</span>
      <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onEdit} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") onEdit(); }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>
          {chore.name}
          <span className="muted" style={{ fontWeight: 400 }}> · {chore.points} pt{chore.points === 1 ? "" : "s"}</span>
          {(() => {
            const ds = choreDueStatus(chore.cadence, lastDoneOn);
            if (!ds || ds.status === "ok") return null;
            const over = ds.status === "overdue";
            return <span style={{ marginLeft: "0.5rem", fontSize: 10, fontWeight: 600, padding: "0.05rem 0.4rem", borderRadius: 999, color: over ? "#b3261e" : "#8a6d1a", background: over ? "rgba(179,38,30,0.12)" : "rgba(201,138,26,0.14)", whiteSpace: "nowrap" }}>{over ? `overdue ${ds.days}d` : "due"}</span>;
          })()}
        </div>
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>
          {turnName && <span style={{ color: "var(--accent)", fontWeight: 600 }}>🔄 {turnName}'s turn · </span>}
          {cadenceLabel(chore.cadence)}
          {doneCount > 0 ? ` · done ${doneCount}×` : " · not logged yet"}
          {lastDoneOn ? ` · last ${relativeDay(lastDoneOn)}` : ""}
        </div>
      </div>
      <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.3rem 0.6rem", gap: "0.3rem", flexShrink: 0 }} onClick={onLog} title={`Log “${chore.name}” done today`}>
        <Check size={13} /> Done
      </button>
      <button className="tile-del" title="Delete chore" aria-label="Delete chore" onClick={onDelete}>
        <Trash2 size={15} />
      </button>
    </div>
  );
}

// ── Completion tile ───────────────────────────────────────────────────────────
function CompletionTile({ c, onEdit, onDelete }: { c: CompletionView; onEdit: () => void; onDelete: () => void }) {
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <div className="tile" onClick={onEdit} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onEdit(); }} style={{ cursor: "pointer" }}>
        <span className="tile-icon" title={c.chore_name}>{emojiForChore(c.chore_name)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>
            {c.chore_name}
            <span className="pill ahead" style={{ marginLeft: "0.5rem" }}>+{c.points} pt{c.points === 1 ? "" : "s"}</span>
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>
            {relativeDay(c.done_on)} · {prettyDate(c.done_on)}
          </div>
          {c.note && <div className="muted" style={{ fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.note}</div>}
        </div>
        {c.photo && (
          <img src={c.photo} alt={`Photo proof of ${c.chore_name}`} title="Tap to view"
            onClick={(ev) => { ev.stopPropagation(); setZoom(true); }}
            style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "var(--radius-sm, 8px)", cursor: "zoom-in", flexShrink: 0, marginRight: "0.4rem" }} />
        )}
        <button className="tile-del" title="Delete entry" aria-label="Delete entry" onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
          <Trash2 size={15} />
        </button>
      </div>
      {zoom && c.photo && (
        <PhotoViewer photos={[{ src: c.photo, alt: `Photo proof of ${c.chore_name}` }]} onClose={() => setZoom(false)} />
      )}
    </>
  );
}

// ── "Log done" picker: which chore did they finish? ───────────────────────────
function LogPicker({ member, chores, onClose, onPick, onAddChore }: {
  member: Member; chores: Chore[]; onClose: () => void; onPick: (ch: Chore) => void; onAddChore: () => void;
}) {
  return (
    <ModalShell title={`What did ${member.name} do?`} onCancel={onClose}>
      {chores.length === 0 ? (
        <div style={{ textAlign: "center", padding: "0.5rem 0 0.25rem" }}>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", marginBottom: "0.9rem" }}>No chores set up yet for {member.name}.</div>
          <button className="btn btn-primary row" style={{ gap: "0.4rem", margin: "0 auto" }} onClick={onAddChore}><Plus size={15} /> Add a chore</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {chores.map((ch) => (
            <button key={ch.id} className="tile" onClick={() => onPick(ch)} style={{ textAlign: "left", cursor: "pointer", width: "100%" }}>
              <span className="tile-icon">{emojiForChore(ch.name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{ch.name}</div>
                <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{cadenceLabel(ch.cadence)} · {ch.points} pt{ch.points === 1 ? "" : "s"}</div>
              </div>
              <Check size={16} style={{ color: "var(--accent)", flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}
    </ModalShell>
  );
}

// ── Forms ─────────────────────────────────────────────────────────────────────
function MemberForm({ editing, onClose, onSaved }: { editing: Member | null; onClose: () => void; onSaved: (savedId?: string) => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setErr("Add a name."); return; }
    setSaving(true); setErr(null);
    try {
      if (isEditing) { await store.updateMember(editing!.id, { name: name.trim() }); onSaved(editing!.id); }
      else { const c = await store.createMember({ name: name.trim() }); onSaved(c.id); }
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit person" : "Add a person"} onCancel={onClose}>
      <Field label="Name">
        <input autoFocus placeholder="e.g. Maya" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void save(); }} />
      </Field>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <FormButtons saving={saving} onCancel={onClose} onSave={save} saveLabel={isEditing ? "Update" : "Add person"} />
    </ModalShell>
  );
}

function ChoreForm({ member, allMembers, editing, onClose, onSaved }: {
  member: Member; allMembers: Member[]; editing: Chore | null; onClose: () => void; onSaved: () => void;
}) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [points, setPoints] = useState(editing?.points != null ? String(editing.points) : "1");
  const [cadence, setCadence] = useState(editing?.cadence ?? "once");
  const [active, setActive] = useState(editing ? editing.active !== 0 : true);
  // Explicit calendar deadline ("" = none). Coexists with cadence; while outstanding it decides
  // the board row, then cadence resumes. A plain date input — the OS picker does the calendar.
  const [dueOn, setDueOn] = useState(editing?.due_on ?? "");
  // Rotation pool (ordered member_ids). Always holds ≥1 (the current member); ≥2 = a rotating chore.
  const [pool, setPool] = useState<string[]>(() => {
    const r = editing ? rotationOrder(editing) : [];
    return r.length ? r : [editing?.member_id ?? member.id];
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const rotating = pool.length >= 2;
  const currentId = editing?.member_id ?? member.id;
  const togglePeer = (id: string) =>
    setPool((p) => (p.includes(id) ? (p.length > 1 ? p.filter((x) => x !== id) : p) : [...p, id]));

  async function save() {
    if (!name.trim()) { setErr("Name the chore."); return; }
    const pts = points.trim() ? Number(points.trim()) : 0;
    if (!Number.isInteger(pts) || pts < 0) { setErr("Points must be a whole number, 0 or more."); return; }
    setSaving(true); setErr(null);
    const rotation = rotating ? pool.join(",") : "";
    // Whose turn now: keep it if still in the pool, else the pool's first.
    const turnId = rotating ? (pool.includes(currentId) ? currentId : pool[0]) : (isEditing ? currentId : member.id);
    try {
      if (isEditing) {
        await store.updateChore(editing!.id, { name: name.trim(), points: pts, cadence: cadence.trim() || "once", active: active ? 1 : 0, rotation, member_id: turnId, due_on: dueOn });
      } else {
        await store.createChore({ member_id: turnId, name: name.trim(), points: pts, cadence: cadence.trim() || "once", rotation, due_on: dueOn });
      }
      onSaved();
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit chore" : `Add a chore for ${member.name}`} onCancel={onClose} wide>
      <Field label="Chore">
        <input autoFocus placeholder="e.g. Feed the dog" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Points" hint="A plain tally you choose.">
            <input inputMode="numeric" placeholder="e.g. 2" value={points} onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ""))} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginTop: "0.45rem" }}>
              {POINT_PRESETS.map((p) => (
                <button key={p} type="button" className={`chip${Number(points) === p ? " selected" : ""}`} onClick={() => setPoints(String(p))}>{p}</button>
              ))}
            </div>
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="How often" optional>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
              {CADENCES.map((c) => (
                <button key={c} type="button" className={`chip${cadence === c ? " selected" : ""}`} onClick={() => setCadence(c)}>{cadenceLabel(c)}</button>
              ))}
            </div>
          </Field>
        </div>
      </div>

      <Field label="Due by" optional hint="A one-off deadline. While it's outstanding it decides the board; once done on or after it, the repeat schedule takes over again.">
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="date"
            aria-label="Due by"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
            style={{ fontSize: 16 }}
          />
          {dueOn && (
            <button type="button" className="chip" onClick={() => setDueOn("")}>Clear</button>
          )}
        </div>
      </Field>

      {allMembers.length >= 2 && (
        <Field label="Rotate between people" optional hint="Pick two or more — the chore takes turns; logging it done passes it to the next person.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
            {allMembers.map((c) => (
              <button key={c.id} type="button" className={`chip${pool.includes(c.id) ? " selected" : ""}`} onClick={() => togglePeer(c.id)}>{c.name}</button>
            ))}
          </div>
          {rotating && (
            <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.4rem" }}>
              🔄 Rotates: {pool.map((id) => allMembers.find((c) => c.id === id)?.name ?? "?").join(" → ")} → …
              {" · "}next up: <b>{allMembers.find((c) => c.id === (pool.includes(currentId) ? currentId : pool[0]))?.name ?? "?"}</b>
            </div>
          )}
        </Field>
      )}

      {isEditing && (
        <Field label="Status" hint="Retired chores keep their history but leave the list.">
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button type="button" className={`chip${active ? " selected" : ""}`} onClick={() => setActive(true)}>Active</button>
            <button type="button" className={`chip${!active ? " selected" : ""}`} onClick={() => setActive(false)}>Retired</button>
          </div>
        </Field>
      )}

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <FormButtons saving={saving} onCancel={onClose} onSave={save} saveLabel={isEditing ? "Update" : "Add chore"} />
    </ModalShell>
  );
}

/**
 * LogForm — record (or edit) a completion: which day, points earned, optional note.
 * Used both for a brand-new log (pass `chore`) and for editing an existing entry
 * (pass `completion`). The chore is fixed at log time; points default to the chore's
 * value but you can adjust this one entry.
 */
function LogForm({ member, chore, completion, onClose, onSaved }: {
  member: Member; chore?: Chore; completion?: CompletionView; onClose: () => void; onSaved: () => void;
}) {
  const isEditing = completion != null;
  const choreName = completion?.chore_name ?? chore?.name ?? "Chore";
  const defaultPoints = completion?.points ?? chore?.points ?? 1;
  const [doneOn, setDoneOn] = useState(completion?.done_on ?? todayLocal());
  const [points, setPoints] = useState(String(defaultPoints));
  const [note, setNote] = useState(completion?.note ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    const pts = points.trim() ? Number(points.trim()) : 0;
    if (!Number.isInteger(pts) || pts < 0) { setErr("Points must be a whole number, 0 or more."); return; }
    if (!doneOn) { setErr("Pick the day it was done."); return; }
    setSaving(true); setErr(null);
    try {
      if (isEditing) {
        await store.updateCompletion(completion!.id, { done_on: doneOn, points: pts, note: note.trim() });
      } else if (chore) {
        await store.createCompletion({ member_id: member.id, chore_id: chore.id, done_on: doneOn, points: pts, note: note.trim() });
      }
      onSaved();
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit entry" : `Log “${choreName}” done`} onCancel={onClose}>
      <div className="row" style={{ gap: "0.5rem", marginBottom: "0.85rem" }}>
        <span className="tile-icon">{emojiForChore(choreName)}</span>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{choreName} <span className="muted" style={{ fontWeight: 400 }}>· for {member.name}</span></div>
      </div>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Day done">
            <input type="date" value={doneOn} max={todayLocal()} onChange={(e) => setDoneOn(e.target.value)} />
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Points">
            <input inputMode="numeric" value={points} onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ""))} />
          </Field>
        </div>
      </div>

      <Field label="Note" optional hint="e.g. did it without being asked, or pitched in on something extra.">
        <textarea rows={2} placeholder="Anything to remember (stored on this device unless you back it up)" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <FormButtons saving={saving} onCancel={onClose} onSave={save} saveLabel={isEditing ? "Update" : "Log it"} />
    </ModalShell>
  );
}

// ── Small shared pieces ───────────────────────────────────────────────────────
// ── THE BOARD (ledger gate 2b — the anti-spam differentiator) ────────────────────────────────
//
// What the user manipulates: a chore — a recurring obligation that belongs to someone in the
// household, or ROTATES between them. The question the app is opened with is "what needs doing,
// and whose turn is it" — never "what chores exist". The old home answered the latter: one member
// at a time, `ORDER BY active DESC, name ASC`, i.e. ALPHABETICAL. You had to tap through each
// person to find out what was outstanding.
//
// Gate 1d — taking the FEATURE from the category leaders, never the look. All three are computed
// from data Chores ALREADY stores; none of it needed a new field:
//   • Tody      — chores DECAY. Not a checkbox: how overdue it is, from `cadence` + last `done_on`.
//   • Flatastic — the duty ROSTER. `rotation` already advances on completion but was invisible.
//   • Sweepy    — fair-share BALANCE: who is actually carrying the place, not just who ticked.
//
// ⚠️ Pure + exported-in-spirit so the ordering can be reasoned about without a browser: given the
// same chores/completions it always returns the same ranking.

type DueStatus = "overdue" | "today" | "soon" | "settled";
export type BoardRow = {
  chore: Chore;
  owner: Member | null;
  status: DueStatus;
  /** Whole days past due. 0 unless status === "overdue". */
  daysOver: number;
  lastDone: string | null;
  /** True when this chore rotates between members — `owner` is whose turn it is NOW. */
  rotating: boolean;
};

const DAY_MS = 86_400_000;
/** Local-midnight day index, so "yesterday" never depends on the time of day. */
function dayIndex(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return Math.floor(new Date(y, (m ?? 1) - 1, d ?? 1).getTime() / DAY_MS);
}
function todayIndex(): number {
  const n = new Date();
  return Math.floor(new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime() / DAY_MS);
}

/** The interval a cadence implies, in days. Unknown/free-text cadence → treated as one-off.
 *  🔴 Deliberately NOT a second copy — see `cadencePeriodDays` in store.ts: the board and the
 *  chore tile must answer "is this due" from the SAME vocabulary or they contradict each other
 *  on the same screen (they did, on "monthly"). */
const cadenceDays = cadencePeriodDays;

export function boardRows(chores: Chore[], completions: CompletionView[], members: Member[]): BoardRow[] {
  const today = todayIndex();
  const lastByChore = new Map<string, string>();
  for (const c of completions) {
    const prev = lastByChore.get(c.chore_id);
    if (!prev || c.done_on > prev) lastByChore.set(c.chore_id, c.done_on);
  }

  const rows: BoardRow[] = chores
    .filter((ch) => ch.active !== 0)
    .map((chore) => {
      const lastDone = lastByChore.get(chore.id) ?? null;
      const every = cadenceDays(chore.cadence);
      let status: DueStatus = "today";
      let daysOver = 0;

      // "By when" beats "how often": an outstanding calendar deadline decides the row. Same
      // single rule the chat and the person page use — never a second due-date implementation.
      const dl = deadlineStatus(chore.due_on, lastDone);
      if (dl) {
        status = dl.status === "overdue" ? "overdue" : dl.status === "due" ? "today" : "soon";
        daysOver = dl.status === "overdue" ? dl.days : 0;
      } else if (every === null) {
        // A one-off is outstanding until it is done once, then it is finished for good.
        status = lastDone ? "settled" : "today";
      } else if (!lastDone) {
        status = "today"; // never done: due now, but not "overdue" — there is no missed date yet
      } else {
        const due = dayIndex(lastDone) + every;
        if (today > due) { status = "overdue"; daysOver = today - due; }
        else if (today === due) status = "today";
        else status = "soon";
      }

      return {
        chore,
        owner: members.find((m) => m.id === chore.member_id) ?? null,
        status,
        daysOver,
        lastDone,
        rotating: chore.rotation.trim() !== "",
      };
    });

  // 🔴 THE ORDERING IS THE GATE. Rank by the DECISION facing the user — how badly something is
  // owed — never alphabetically and never by insertion. Ties break on the bigger job (points),
  // so the heavy overdue thing outranks a trivial one that slipped the same number of days.
  const rank: Record<DueStatus, number> = { overdue: 0, today: 1, soon: 2, settled: 3 };
  return rows.sort((a, b) =>
    rank[a.status] - rank[b.status] ||
    b.daysOver - a.daysOver ||
    b.chore.points - a.chore.points ||
    a.chore.name.localeCompare(b.chore.name),
  );
}

/** Fair-share: points each member logged in the window. Sweepy's question — who is carrying it. */
export function balanceByMember(completions: CompletionView[], members: Member[], days = PERIOD_DAYS) {
  const cutoff = todayIndex() - days;
  const pts = new Map<string, number>();
  for (const c of completions) {
    if (dayIndex(c.done_on) < cutoff) continue;
    pts.set(c.member_id, (pts.get(c.member_id) ?? 0) + c.points);
  }
  const total = [...pts.values()].reduce((a, b) => a + b, 0);
  return members
    .map((m) => ({ member: m, points: pts.get(m.id) ?? 0, share: total > 0 ? (pts.get(m.id) ?? 0) / total : 0 }))
    .sort((a, b) => b.points - a.points);
}

function Field({ label, optional, hint, children }: { label: string; optional?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: "0.85rem" }}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>
        {label} {optional && <span className="muted" style={{ fontWeight: 400 }}>(optional)</span>}
      </div>
      {children}
      {hint && <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function FormButtons({ saving, onCancel, onSave, saveLabel }: { saving: boolean; onCancel: () => void; onSave: () => void; saveLabel: string }) {
  return (
    <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.25rem" }}>
      <button className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? "Saving…" : saveLabel}</button>
    </div>
  );
}

function ConfirmDelete({ title, body, onCancel, onConfirm }: { title: string; body: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <ModalShell title={title} onCancel={onCancel}>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem", lineHeight: 1.5 }}>{body}</p>
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
        <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={onCancel}>Cancel</button>
        <button className="btn btn-danger" style={{ fontSize: "var(--fs-xs)" }} onClick={onConfirm}>Delete</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onCancel} />
      <div role="dialog" aria-modal="true" aria-label={title}
        className="sheet sheet-center" style={{ width: wide ? "min(480px, calc(100% - 2rem))" : undefined }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </>
  );
}
